import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

export function DirectDepositsAdminTab({ active }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("pending_approval");
  const [thresholdInput, setThresholdInput] = useState("");
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [noteById, setNoteById] = useState({});

  const load = async (statusFilter = filter) => {
    setLoading(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const r = await api.get("/admin/direct-deposits", { params });
      setData(r.data);
      setThresholdInput(String(r.data.threshold_usd ?? ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load wire deposits");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) load(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, filter]);

  const saveThreshold = async () => {
    const n = Number(thresholdInput);
    if (Number.isNaN(n) || n < 0) {
      toast.error("Enter a non-negative number");
      return;
    }
    setSavingThreshold(true);
    try {
      await api.put("/admin/direct-deposits/threshold", { threshold_usd: n });
      toast.success(`Auto-approval threshold set to $${n.toLocaleString()}`);
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save threshold");
    } finally {
      setSavingThreshold(false);
    }
  };

  const decide = async (id, action) => {
    setBusyId(id);
    try {
      await api.post(`/admin/direct-deposits/${id}/${action}`, { note: noteById[id] || null });
      toast.success(action === "approve" ? "Deposit approved · receipt sent" : "Deposit rejected");
      setNoteById((m) => ({ ...m, [id]: "" }));
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${action} deposit`);
    } finally {
      setBusyId(null);
    }
  };

  const downloadPdf = async (id, ref) => {
    try {
      const r = await api.get(`/direct-deposits/${id}/receipt.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `zcardon-deposit-${ref}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download receipt");
    }
  };

  const summary = data?.summary || { pending_approval: 0, approved: 0, rejected: 0 };
  const feedback = data?.feedback_7d || null;
  const rows = data?.deposits || [];

  return (
    <div className="space-y-6" data-testid="admin-wires-panel">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryTile label="Pending approval" value={summary.pending_approval} accent="#CBA35C" testid="wires-pending-tile" />
        <SummaryTile label="Approved" value={summary.approved} accent="#184A30" testid="wires-approved-tile" />
        <SummaryTile label="Rejected" value={summary.rejected} accent="#D96C4A" testid="wires-rejected-tile" />
      </div>

      <ReceiverFeedbackTile feedback={feedback} />

      <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Auto-approval threshold</p>
        <p className="text-sm text-[#4A5C50] mt-1">
          Direct deposits below this USD amount are auto-approved with an instant receipt.
          Anything at or above queues here for manual review.
        </p>
        <div className="mt-3 flex items-end gap-2 max-w-md">
          <div className="flex-1">
            <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">Threshold (USD)</Label>
            <Input
              type="number"
              min="0"
              step="1"
              value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)}
              className="mt-1 mono"
              data-testid="wires-threshold-input"
            />
          </div>
          <Button
            onClick={saveThreshold}
            disabled={savingThreshold}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="wires-threshold-save"
          >
            {savingThreshold ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <WireOpsTools />

      <div className="flex items-center gap-2">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50] mr-2">Show</p>
        {[
          { key: "pending_approval", label: "Pending" },
          { key: "approved", label: "Approved" },
          { key: "rejected", label: "Rejected" },
          { key: "all", label: "All" },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setFilter(opt.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              filter === opt.key
                ? "bg-[#184A30] border-[#184A30] text-white"
                : "bg-white border-[#E5E0D8] text-[#4A5C50] hover:border-[#184A30]"
            }`}
            data-testid={`wires-filter-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-[#E5E0D8] bg-white">
        {loading && (
          <p className="p-6 text-sm text-[#4A5C50]" data-testid="wires-loading">Loading deposits…</p>
        )}
        {!loading && rows.length === 0 && (
          <p className="p-6 text-sm text-[#4A5C50]" data-testid="wires-empty">No deposits in this view.</p>
        )}
        {!loading && rows.length > 0 && (
          <ul className="divide-y divide-[#E5E0D8]">
            {rows.map((d) => (
              <li key={d.id} className="p-4 space-y-2" data-testid={`wires-row-${d.reference_number}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="serif text-lg text-[#112318]">${Number(d.amount).toFixed(2)} {d.currency}</span>
                  <span className={`text-[10px] mono px-1.5 py-0.5 rounded ${
                    d.status === "approved"
                      ? "bg-[#184A30]/10 text-[#184A30]"
                      : d.status === "pending_approval"
                      ? "bg-[#CBA35C]/15 text-[#8C6B21]"
                      : "bg-[#D96C4A]/15 text-[#B04E2F]"
                  }`}>
                    {d.status}
                  </span>
                  <span className="text-[11px] mono text-[#4A5C50]">{d.reference_number}</span>
                  <span className="text-[11px] mono text-[#4A5C50] ml-auto">
                    {new Date(d.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-[12px] text-[#4A5C50]">
                  <p><span className="text-[#112318]">Sender:</span> {d.sender_name} <span className="opacity-70">({d.sender_email})</span></p>
                  <p><span className="text-[#112318]">Receiver:</span> {d.receiver_name} {d.receiver_email && <span className="opacity-70">({d.receiver_email})</span>}{d.receiver_phone && <span className="opacity-70"> · {d.receiver_phone}</span>}</p>
                  <p><span className="text-[#112318]">Bank:</span> {d.bank_name}{d.bank_address && <span className="opacity-70"> · {d.bank_address}</span>}</p>
                  <p><span className="text-[#112318]">Routing:</span> <span className="mono">{d.routing_number}</span>{d.swift_code && <span className="ml-2"><span className="text-[#112318]">SWIFT:</span> <span className="mono">{d.swift_code}</span></span>}</p>
                  <p><span className="text-[#112318]">Account:</span> <span className="mono">{d.account_number}</span></p>
                  {d.memo && <p className="md:col-span-2"><span className="text-[#112318]">Memo:</span> {d.memo}</p>}
                  {d.admin_note && <p className="md:col-span-2"><span className="text-[#112318]">Admin note:</span> {d.admin_note}</p>}
                  {d.approved_by && <p><span className="text-[#112318]">Approved by:</span> {d.approved_by} {d.approved_at && <span className="opacity-70">· {new Date(d.approved_at).toLocaleString()}</span>}</p>}
                  {d.rejected_by && <p><span className="text-[#112318]">Rejected by:</span> {d.rejected_by} {d.rejected_at && <span className="opacity-70">· {new Date(d.rejected_at).toLocaleString()}</span>}</p>}
                  {d.receiver_confirmed_at && (
                    <p className="md:col-span-2"><span className="text-[#184A30] font-semibold">✓ Receiver confirmed</span> <span className="opacity-70">· {new Date(d.receiver_confirmed_at).toLocaleString()}</span></p>
                  )}
                  {d.receiver_disputed_at && (
                    <p className="md:col-span-2"><span className="text-[#B04E2F] font-semibold">⚠ Receiver disputed</span> <span className="opacity-70">· {new Date(d.receiver_disputed_at).toLocaleString()}</span>{d.receiver_dispute_reason && <span className="block italic mt-0.5">"{d.receiver_dispute_reason}"</span>}</p>
                  )}
                  {(d.verification_views || 0) > 0 && (
                    <p className="md:col-span-2 text-[#184A30]" data-testid={`wires-views-${d.reference_number}`}>
                      <span className="font-semibold">👁 Receipt verified online {d.verification_views}× </span>
                      <span className="opacity-70">
                        · last opened {d.verification_last_view_at ? new Date(d.verification_last_view_at).toLocaleString() : "—"}
                      </span>
                    </p>
                  )}
                  {d.receiver_follow_up_sent_at && (
                    <p className="md:col-span-2 text-[#8C6B21]" data-testid={`wires-followup-${d.reference_number}`}>
                      <span className="font-semibold">📨 T+72h follow-up sent</span>
                      <span className="opacity-70">
                        {" "}· {new Date(d.receiver_follow_up_sent_at).toLocaleString()}
                        {Array.isArray(d.receiver_follow_up_channels) && d.receiver_follow_up_channels.length > 0 && ` · via ${d.receiver_follow_up_channels.join(" + ")}`}
                      </span>
                    </p>
                  )}
                  {(d.sender_notified_portal_opened_at || d.sender_notified_confirmed_at || d.sender_notified_disputed_at) && (
                    <p className="md:col-span-2 text-[#4A5C50] text-[11px]" data-testid={`wires-sender-notified-${d.reference_number}`}>
                      <span className="font-semibold text-[#184A30]">📡 Sender pinged:</span>
                      {d.sender_notified_portal_opened_at && <span className="ml-2">portal-opened ✓</span>}
                      {d.sender_notified_confirmed_at && <span className="ml-2">confirmed ✓✓</span>}
                      {d.sender_notified_disputed_at && <span className="ml-2 text-[#B04E2F]">disputed ⚠</span>}
                    </p>
                  )}
                </div>
                {d.status === "pending_approval" && (
                  <div className="pt-2 space-y-2">
                    <Textarea
                      placeholder="Optional admin note (visible in audit log)…"
                      rows={2}
                      value={noteById[d.id] || ""}
                      onChange={(e) => setNoteById((m) => ({ ...m, [d.id]: e.target.value }))}
                      data-testid={`wires-note-${d.reference_number}`}
                    />
                    <div className="flex gap-2">
                      <Button
                        onClick={() => decide(d.id, "approve")}
                        disabled={busyId === d.id}
                        className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
                        data-testid={`wires-approve-${d.reference_number}`}
                      >
                        {busyId === d.id ? "Working…" : "Approve & email receipt"}
                      </Button>
                      <Button
                        onClick={() => decide(d.id, "reject")}
                        disabled={busyId === d.id}
                        variant="outline"
                        className="rounded-xl border-[#D96C4A]/40 text-[#B04E2F] hover:bg-[#D96C4A]/10"
                        data-testid={`wires-reject-${d.reference_number}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                )}
                {d.status === "approved" && (
                  <div className="pt-1 flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(d.id, d.reference_number)}
                      className="rounded-lg"
                      data-testid={`wires-pdf-${d.reference_number}`}
                    >
                      Download PDF receipt
                    </Button>
                    {d.receiver_token && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = `${window.location.origin}/deposit/${d.receiver_token}`;
                          if (navigator.clipboard) navigator.clipboard.writeText(url);
                          toast.success("Receiver portal link copied");
                        }}
                        className="rounded-lg"
                        data-testid={`wires-copy-link-${d.reference_number}`}
                      >
                        Copy receiver link
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent, testid }) {
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid={testid}>
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{label}</p>
      <p className="serif text-4xl mt-2" style={{ color: accent }}>{value}</p>
    </div>
  );
}

function ReceiverFeedbackTile({ feedback }) {
  if (!feedback) return null;
  const { approved, confirmed, disputed, awaiting,
          confirmation_rate_pct, dispute_rate_pct, awaiting_rate_pct } = feedback;
  const empty = approved === 0;
  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5" data-testid="wires-feedback-tile">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Receiver feedback · last 7 days</p>
          <p className="text-sm text-[#4A5C50] mt-1">
            How recipients are responding once they open the receipt portal.
          </p>
        </div>
        {!empty && (
          <p className="serif text-4xl text-[#184A30]" data-testid="wires-feedback-confirm-rate">
            {confirmation_rate_pct ?? 0}%
            <span className="text-sm text-[#4A5C50] ml-2">confirmed</span>
          </p>
        )}
      </div>
      {empty && (
        <p className="text-sm text-[#4A5C50] mt-4" data-testid="wires-feedback-empty">
          No deposits approved in the last 7 days yet.
        </p>
      )}
      {!empty && (
        <>
          <div className="mt-4 h-3 rounded-full overflow-hidden bg-[#FBF9F6] border border-[#E5E0D8] flex" data-testid="wires-feedback-bar">
            {confirmed > 0 && (
              <div className="h-full bg-[#184A30]" style={{ width: `${confirmation_rate_pct}%` }} title={`${confirmed} confirmed`} />
            )}
            {disputed > 0 && (
              <div className="h-full bg-[#D96C4A]" style={{ width: `${dispute_rate_pct}%` }} title={`${disputed} disputed`} />
            )}
            {awaiting > 0 && (
              <div className="h-full bg-[#CBA35C]/40" style={{ width: `${awaiting_rate_pct}%` }} title={`${awaiting} awaiting`} />
            )}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 text-[11px] mono text-[#4A5C50]">
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#184A30]" /> Confirmed <span className="ml-auto text-[#112318]">{confirmed}</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#D96C4A]" /> Disputed <span className="ml-auto text-[#112318]">{disputed}</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#CBA35C]/60" /> Awaiting <span className="ml-auto text-[#112318]">{awaiting}</span></div>
          </div>
          <p className="text-[11px] text-[#4A5C50] mt-3">
            Out of <b className="text-[#112318]">{approved}</b> deposit{approved === 1 ? "" : "s"} approved in the window.
          </p>
        </>
      )}
    </div>
  );
}

function WireOpsTools() {
  const [busy, setBusy] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const sendDigest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/direct-deposits/digest/send-now", {
        override_email: overrideEmail.trim() || null,
      });
      if (r.data?.ok && (r.data.sent || []).length) {
        toast.success(`Digest sent to ${r.data.sent.length} recipient${r.data.sent.length === 1 ? "" : "s"}`);
      } else if (r.data?.reason) {
        toast.error(`Digest not sent: ${r.data.reason}`);
      } else {
        toast.message("Digest dispatched");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send digest");
    } finally {
      setBusy(false);
    }
  };

  const downloadCsv = async () => {
    setBusy(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await api.get("/admin/direct-deposits/export.csv", {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const tag = (start || "all") + "-" + (end || "all");
      a.download = `zcardon-direct-deposits-${tag}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not download CSV — check date format");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 space-y-5" data-testid="wires-ops-tools">
      <div>
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Daily digest</p>
        <p className="text-sm text-[#4A5C50] mt-1">
          A short summary of yesterday's wire-deposit activity is auto-emailed to all admins each morning (UTC).
          Trigger an ad-hoc send below — leave the override blank to send to every admin, or set it to preview the digest in your own inbox.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">Override email (optional)</Label>
            <Input
              type="email"
              value={overrideEmail}
              onChange={(e) => setOverrideEmail(e.target.value)}
              placeholder="you@yourcompany.com"
              className="mt-1"
              data-testid="wires-digest-override-email"
            />
          </div>
          <Button
            onClick={sendDigest}
            disabled={busy}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="wires-digest-send-btn"
          >
            {busy ? "Sending…" : "Send digest now"}
          </Button>
        </div>
      </div>

      <div className="border-t border-[#E5E0D8] pt-5">
        <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]">Export to CSV</p>
        <p className="text-sm text-[#4A5C50] mt-1">
          Download a CSV of every direct deposit (with full bank details) for accounting reconciliation.
          Leave both dates blank to export everything.
        </p>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">Start date</Label>
            <Input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 mono"
              data-testid="wires-export-start"
            />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-[#4A5C50]">End date</Label>
            <Input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 mono"
              data-testid="wires-export-end"
            />
          </div>
          <Button
            onClick={downloadCsv}
            disabled={busy}
            variant="outline"
            className="rounded-xl mt-auto h-10 border-[#184A30]/30 text-[#184A30] hover:bg-[#184A30]/5"
            data-testid="wires-export-btn"
          >
            Download CSV
          </Button>
        </div>
      </div>
    </div>
  );
}
